function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _toConsumableArray(r) { return _arrayWithoutHoles(r) || _iterableToArray(r) || _unsupportedIterableToArray(r) || _nonIterableSpread(); }
function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _iterableToArray(r) { if ("undefined" != typeof Symbol && null != r[Symbol.iterator] || null != r["@@iterator"]) return Array.from(r); }
function _arrayWithoutHoles(r) { if (Array.isArray(r)) return _arrayLikeToArray(r); }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { useQuery } from '@apollo/client';
import { getClientEnvironmentDetails } from '@sm/utils';
import { useCallback, useMemo } from 'react';
import experimentsQuery from './Experiments.graphql';
import useSaveAssignment from './useSaveAssignment';

/**
 * Converts an experimentsvc experiment response to a TreatmentItem object
 *
 * @param experiment The experiment object from experimentsvc to format
 * @returns The formatted TreatmentItem object
 */
function formatExperimentAsTreatmentItem(experiment) {
  return {
    treatmentName: experiment.treatment.name,
    treatmentId: experiment.treatment.id,
    experimentId: experiment.id,
    experimentName: experiment.name,
    assignmentType: experiment.assignmentType
  };
}
export default function useExperiment(_ref) {
  var name = _ref.name,
    treatmentNames = _ref.treatmentNames,
    controlName = _ref.controlName;
  var _getClientEnvironment = getClientEnvironmentDetails(),
    isBrowser = _getClientEnvironment.isBrowser;
  var pageReferer = isBrowser ? document.referrer : '/';
  var pageUrl = isBrowser ? window.location.href : '/';

  // convert the name to an array without whitespace if it is not already...
  // ...which it shouldn't be because types, but we can't assume TS everywhere
  var experimentNames = Array.isArray(name) ? name : [name.trim()];
  var _useQuery = useQuery(experimentsQuery, {
      fetchPolicy: 'no-cache',
      variables: {
        pageReferer: pageReferer,
        pageUrl: pageUrl,
        experimentNames: experimentNames
      }
    }),
    data = _useQuery.data,
    loading = _useQuery.loading;
  var saveAssignment = useSaveAssignment();
  var _useMemo = useMemo(function () {
      var _returnedAssignment;
      var treatmentMap = {};
      var returnedAssignment;
      if (!loading && Array.isArray(data === null || data === void 0 ? void 0 : data.experiments)) {
        // load up the actual real data that we have...
        data.experiments.forEach(function (exp) {
          var treatmentItem = formatExperimentAsTreatmentItem(exp);
          treatmentMap[exp.treatment.name] = treatmentItem;
          returnedAssignment = treatmentItem;
        });

        // ...and toss in some perfunctory empty objects for any other treatments that were
        // requested from the hook but not returned from the server
        [].concat(_toConsumableArray(treatmentNames !== null && treatmentNames !== void 0 ? treatmentNames : []), [controlName]).forEach(function (treatmentName) {
          if (treatmentName) {
            var _treatmentMap$treatme;
            treatmentMap[treatmentName] = (_treatmentMap$treatme = treatmentMap[treatmentName]) !== null && _treatmentMap$treatme !== void 0 ? _treatmentMap$treatme : {};
          }
        });
      }
      return {
        treatmentNameMap: treatmentMap,
        experimentName: (_returnedAssignment = returnedAssignment) === null || _returnedAssignment === void 0 ? void 0 : _returnedAssignment.experimentName,
        assignment: returnedAssignment
      };
    }, [controlName, data, loading, treatmentNames]),
    treatmentNameMap = _useMemo.treatmentNameMap,
    experimentName = _useMemo.experimentName,
    assignment = _useMemo.assignment;
  var getAssignment = useCallback(function () {
    if (loading) {
      return null;
    }

    // if the assignment object is trying to be accessed, the experiment is being acted upon,
    // so ensure that the assignment gets saved
    if (assignment) {
      saveAssignment(assignment);
    }

    // return a copy of the object so folks on the other side can't do shenanigans
    return assignment ? _objectSpread({}, assignment) : null;
  }, [loading, assignment, saveAssignment]);
  var isTreatment = useCallback(function (treatmentName) {
    if (loading) {
      return false;
    }
    var activeAssignment = getAssignment();
    return (activeAssignment === null || activeAssignment === void 0 ? void 0 : activeAssignment.treatmentName) === treatmentName;
  }, [loading, getAssignment]);
  var isControl = useCallback(function () {
    // if no control treatment name was provided, we can't meaningfully determine this condition
    if (!controlName) {
      throw new Error('No control treatment name provided');
    }
    if (loading) {
      return false;
    }

    // no assignment or assigned to control is control for functional purposes
    return !assignment || isTreatment(controlName);
  }, [controlName, loading, assignment, isTreatment]);

  // we only support one experiment at a time... for now. A multi-experiment
  // hook will need a completely different shape
  if (experimentNames.length > 1) {
    throw new Error('useExperiment only supports loading one experiment');
  }
  return {
    name: experimentName,
    treatments: treatmentNameMap,
    loading: loading,
    isControl: isControl,
    isTreatment: isTreatment,
    getAssignment: getAssignment
  };
}
//# sourceMappingURL=useExperiment.js.map